import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { showErrorNotification } from "molecules/notifications/notificationEvents";
import { openPendingTab } from "src/common/utils/openPendingTab";
import { eox7ApiUrl } from "src/env";
import {
	useCreateOpenTokenMutation,
	useCreateViewTokenMutation,
} from "../api/excelReportsApi";
import type { DomainDocumentItem } from "../api/types";
import {
	buildDocumentViewUrl,
	buildExcelOpenUri,
} from "../utils/excelReportUrls";
import {
	getReportOpenMode,
	lacksExcelForDesktop,
} from "../utils/reportOpenMode";
import { type Eox7Endpoint, reportEox7Error } from "../utils/reportEox7Error";

export type ExcelReportFile = Pick<DomainDocumentItem, "id" | "name">;

type TokenEndpoint = Extract<Eox7Endpoint, "open-token" | "view-token">;

/** A failed mint has no token to leak into the report. */
const unwrapOrReport = async <T>(
	request: { unwrap: () => Promise<T> },
	endpoint: TokenEndpoint,
): Promise<T> => {
	try {
		return await request.unwrap();
	} catch (error) {
		reportEox7Error(error, endpoint);
		throw error;
	}
};

/**
 * Opens a stored report: Excel workbooks in Excel for desktop with the add-in
 * injected, PDF and Word files in a new tab. Call `openReport` straight from
 * the click handler so the navigation keeps the click's user activation.
 * It resolves to whether the report was handed off, so the caller can follow
 * up on a launch it can't observe.
 */
export function useOpenExcelReport(companyDomainId: string) {
	const { t } = useTranslation();
	const [createOpenToken] = useCreateOpenTokenMutation();
	const [createViewToken] = useCreateViewTokenMutation();
	// The ref guards against double clicks before the re-render lands.
	const openingIdsRef = useRef(new Set<number>());
	const [openingIds, setOpeningIds] = useState<ReadonlySet<number>>(new Set());

	const setOpening = (documentId: number, opening: boolean) => {
		if (opening) {
			openingIdsRef.current.add(documentId);
		} else {
			openingIdsRef.current.delete(documentId);
		}
		setOpeningIds(new Set(openingIdsRef.current));
	};

	const getDisabledReason = (file: ExcelReportFile): string | null => {
		const mode = getReportOpenMode(file.name);
		if (mode === null) return t("This file type can't be opened here");
		if (mode === "excel" && lacksExcelForDesktop()) {
			return t("Requires Excel for desktop");
		}
		return null;
	};

	const openInExcel = async (documentId: number) => {
		const documentRef = { companyDomainId, documentId };
		const { token, filename } = await unwrapOrReport(
			createOpenToken(documentRef),
			"open-token",
		);
		window.location.href = buildExcelOpenUri({
			baseUrl: eox7ApiUrl,
			...documentRef,
			token,
			filename,
		});
	};

	const openInBrowser = async (documentId: number) => {
		const pendingTab = openPendingTab();
		const documentRef = { companyDomainId, documentId };
		try {
			const { token } = await unwrapOrReport(
				createViewToken(documentRef),
				"view-token",
			);
			pendingTab.navigate(
				buildDocumentViewUrl({ baseUrl: eox7ApiUrl, ...documentRef, token }),
			);
		} catch (error) {
			pendingTab.cancel();
			throw error;
		}
	};

	const openReport = async (file: ExcelReportFile): Promise<boolean> => {
		if (getDisabledReason(file) || openingIdsRef.current.has(file.id)) {
			return false;
		}

		setOpening(file.id, true);
		try {
			if (getReportOpenMode(file.name) === "excel") {
				await openInExcel(file.id);
			} else {
				await openInBrowser(file.id);
			}
			return true;
		} catch {
			showErrorNotification({ message: t("The report could not be opened") });
			return false;
		} finally {
			setOpening(file.id, false);
		}
	};

	const isOpening = (documentId: number) => openingIds.has(documentId);

	return { openReport, isOpening, getDisabledReason };
}
